import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import { RequireAuth } from './auth/RequireAuth';
import { AppShell } from './layout/AppShell';
import { LoginPage } from './routes/LoginPage';
import { UnauthorizedPage } from './routes/UnauthorizedPage';
import { LeadsPage } from './routes/LeadsPage';
import { LeadDetailPage } from './routes/LeadDetailPage';
import { TemplateQaPage } from './routes/TemplateQaPage';
import { WizardRoute } from './wizard/WizardRoute';
import { EditPage } from './routes/EditPage';
import { CallSheet } from './routes/lead/CallSheet';
import { HistoryTab } from './routes/lead/HistoryTab';
import { ProvisioningTab } from './routes/lead/ProvisioningTab';
import { useLeadOutlet } from './routes/lead/useLeadOutlet';
import { NotFoundPage } from './routes/NotFoundPage';
import { PrintOptionsPage } from './routes/PrintOptionsPage';

function RootProviders() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

function OverviewTabRoute() {
  const { lead } = useLeadOutlet();
  return <CallSheet lead={lead} />;
}
function HistoryTabRoute() {
  const { lead } = useLeadOutlet();
  return <HistoryTab leadId={lead.id} />;
}
function ProvisioningTabRoute() {
  const { lead } = useLeadOutlet();
  return <ProvisioningTab lead={lead} />;
}

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  {
    element: <RootProviders />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/unauthorized', element: <UnauthorizedPage /> },
      {
        // Standalone printable view - intentionally outside AppShell so the
        // app chrome doesn't show up when the user prints or saves to PDF.
        path: 'leads/:id/print-options',
        element: (
          <RequireAuth>
            <PrintOptionsPage />
          </RequireAuth>
        ),
      },
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/leads" replace /> },
          {
            path: 'leads',
            element: (
              <RequireAuth>
                <LeadsPage />
              </RequireAuth>
            ),
          },
          {
            path: 'template-qa',
            element: (
              <RequireAuth>
                <TemplateQaPage />
              </RequireAuth>
            ),
          },
          {
            path: 'leads/:id',
            element: (
              <RequireAuth>
                <LeadDetailPage />
              </RequireAuth>
            ),
            children: [
              { index: true, element: <Navigate to="overview" replace /> },
              { path: 'overview', element: <OverviewTabRoute /> },
              { path: 'history', element: <HistoryTabRoute /> },
              { path: 'provisioning', element: <ProvisioningTabRoute /> },
            ],
          },
          {
            path: 'leads/:id/wizard',
            element: (
              <RequireAuth>
                <WizardRoute />
              </RequireAuth>
            ),
          },
          {
            path: 'leads/:id/edit',
            element: (
              <RequireAuth>
                <EditPage />
              </RequireAuth>
            ),
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
